<template>
  <form novalidate @submit.prevent="handleSubmitLogin">
    <h2 class="login__title">{{ $t(`${namespace}.login`) }}</h2>
    <p class="form__text">{{ $t(`${namespace}.loginText`) }}</p>

    <FormTextField
      v-model="form.email"
      type="email"
      :label="$t(`${namespace}.form.emailLabel`)"
      :placeholder="$t(`${namespace}.form.emailPlaceholder`)"
      :disabled="submitted"
      :name="$t(`${namespace}.form.emailName`)"
      :v-validate="{ required: true, email: true }"
    />

    <FormTextField
      v-model="form.password"
      type="password"
      :label="$t(`${namespace}.form.passwordLabel`)"
      :placeholder="$t(`${namespace}.form.passwordPlaceholder`)"
      :disabled="submitted"
      :name="$t(`${namespace}.form.passwordName`)"
      :v-validate="{ required: true }"
    />

    <v-layout align-center justify-center wrap fill-height>
      <v-flex xs6>
        <a
          href="#"
          v-html="$t(`${namespace}.forgotPassword`)"
          @click.prevent="toggleForm"
        />
      </v-flex>

      <v-flex xs6>
        <Button
          type="submit"
          color="secondary"
          gradient
          :disabled="submitted"
          :loading="submitted"
          block
          large
        >
          {{ $t(`${namespace}.form.submitButton`) }}
        </Button>
      </v-flex>
    </v-layout>

    <div class="login__or">
      <span>{{ $t(`${namespace}.or`) }}</span>
    </div>

    <div class="login_disclaimer text--center">
      {{ $t(`${namespace}.notAclientYet`) }} <a :href="smbUrl">{{ $t(`${namespace}.signUpNow`) }}</a>
    </div>
  </form>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Cookie from 'corp-front-vue-commons/src/utils/Cookie'
import jwt from 'corp-front-vue-commons/src/utils/jwt'
import moment from 'libs/moment'
import session from '@/services/modules/session'
import {
  SESSION_USER,
  SESSION_HEADERS,
  SESSION_TIME,
  SESSION_EXPIRES_TIME_VALUE,
  SESSION_EXPIRES_TIME_UNIT,
  REDIRECT_PATH_ON_AUTH,
  SESSION_SKIP_WIZARD
} from 'utils/constants'
import { hasRole } from 'utils/functions'
import { isValidArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import pendoInitialize from 'libs/pendo'

export default {
  name: 'LoginForm',
  computed: {
    ...mapState({
      globalCompany: ({ company }) => company.selectedCompany
    }),

    smbUrl () {
      return process.env.NODE_ENV === 'production'
        ? 'https://corp.99app.com'
        : 'https://b2b-onboarding.staging.99taxis.com'
    }
  },
  data () {
    return {
      namespace: 'Components.LoginForm',
      form: {
        email: null,
        password: null
      },
      submitted: false,
      costCenterLength: 0,
      employeesLength: 0,
      hasDefaultCostCenter: false
    }
  },
  methods: {
    ...mapActions({
      loginRequest: 'session/loginRequest',
      loginSuccess: 'session/loginSuccess',
      loginFailure: 'session/loginFailure',
      addUserClaims: 'claims/add',
      updateSelectedCompany: 'company/updateSelectedCompany',
      setSessionExpiresTime: 'sessionExpiresTime/set',
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    hasRole (group, role) {
      return hasRole(group, role)
    },

    toggleForm () {
      this.$emit('toggleForm')
    },

    setSubmittedValue (boolean = true) {
      this.submitted = boolean
    },

    async handleSubmitLogin () {
      const isValid = await this.$validator.validateAll()

      if (!isValid) return

      this.setSubmittedValue(true)

      try {
        const { email, password } = this.form
        this.loginRequest({ email })

        const { data: res } = await session.login({ email, password })

        if (res && res.valid) {
          const token = res.content
          const { claims, noClaims, customClaims } = this.decodedToken(token)
          const sessionExpiresDate = moment().add(SESSION_EXPIRES_TIME_VALUE, SESSION_EXPIRES_TIME_UNIT).format()
          const userCompanyId = noClaims?.business?.companies && noClaims.business.companies[0]

          this.persistUserData({ claims, noClaims, customClaims, token })
          this.setSessionExpiresTime(sessionExpiresDate)
          await this.makeSimultaneousRequests(userCompanyId)
          this.pendoInitialize(noClaims, claims)
          this.loginSuccess(noClaims)
          this.redirectToPrivateRoute()
          this.setSubmittedValue(false)
          return
        }

        const message = res && res.reasons
          ? res.reasons[0]
          : this.$t('Common.genericError')

        this.setSubmittedValue(false)
        this.loginFailure(message)
        this.showAlert({ type: 'error', message })
        this.gaEventDispatch('login_error', { email: this.email, message })
      } catch (error) {
        const message = error && error.response && error.response.data && error.response.data.reasons
          ? error.response.data.reasons[0]
          : this.$t('Common.genericError')

        catchError(error)
        this.setSubmittedValue(false)
        this.loginFailure(message)
        this.gaEventDispatch('login_error', { email: this.email, message })

        if (error && error.response && error.response.status === 401) {
          return this.getAttemptsRemaining(message)
        }

        this.showAlert({ type: 'error', message })
      }
    },

    decodedToken (token = {}) {
      const { claims = [], ...noClaims } = jwt.decode(token)
      const customClaims = noClaims && noClaims.business && noClaims.business.customClaims
        ? noClaims.business.customClaims
        : []

      return {
        claims,
        noClaims,
        customClaims
      }
    },

    persistUserData ({ claims, noClaims, customClaims, token }) {
      Cookie.set(SESSION_USER, JSON.stringify(noClaims), SESSION_TIME)
      localStorage.setItem(SESSION_HEADERS, JSON.stringify({ authorization: token }))
      this.addUserClaims(claims.concat(customClaims))
    },

    async makeSimultaneousRequests (companyId) {
      try {
        const ROLE_ADMINISTRATOR = this.hasRole('profiles', 'ROLE_ADMINISTRATOR')
        const ROLE_FINANCIAL_ADMIN = this.hasRole('profiles', 'ROLE_FINANCIAL_ADMIN')
        const ROLE_EMPLOYEE_USER = this.hasRole('profiles', 'ROLE_EMPLOYEE_USER')
        const ROLE_GET_COSTCENTER = this.hasRole('costCenter', 'ROLE_GET_COSTCENTER')

        if (ROLE_ADMINISTRATOR || ROLE_FINANCIAL_ADMIN) {
          if (ROLE_GET_COSTCENTER) {
            return await Promise.all([
              this.is99Admin(),
              this.setCompanyUser(1),
              this.costCenterCounter(1)
            ])
          }

          return await Promise.all([
            this.is99Admin(),
            this.setCompanyUser(1)
          ])
        }

        if (ROLE_EMPLOYEE_USER) {
          return this.setCompanyUser(companyId)
        }

        if (!ROLE_GET_COSTCENTER) {
          return await Promise.all([
            this.setCompanyUser(companyId),
            this.costCenterCounter(companyId),
            this.getDefaultCostCenter(companyId),
            this.employeesCounter(companyId)
          ])
        }

        await Promise.all([
          this.setCompanyUser(companyId),
          this.employeesCounter(companyId)
        ])
      } catch (error) {
        catchError(error)
      }
    },

    async is99Admin () {
      try {
        const companyId = 1
        const { data: res } = await this.$apiService.company.getById(companyId)
        const company = res.data
        this.updateSelectedCompany(company)
      } catch (error) {
        catchError(error)
      }
    },

    async setCompanyUser (id) {
      try {
        const ROLE_GET_COMPANY_ID_CONFIG = this.hasRole('company', 'ROLE_GET_COMPANY_ID_CONFIG')
        const ROLE_GET_FINANCIAL_CONFIG = this.hasRole('billing', 'ROLE_GET_FINANCIAL_CONFIG')
        const { data: company } = await this.$apiService.company.getById(id)
        let plan = null

        if (ROLE_GET_COMPANY_ID_CONFIG) {
          const { data: companyConfig } = await this.$apiService.company.getConfig(id)
          plan = companyConfig && companyConfig.data && companyConfig.data.plan
            ? companyConfig.data.plan
            : null
        }

        if (ROLE_GET_FINANCIAL_CONFIG) {
          await this.getWalletFinancialConfig({
            ...company.data,
            plan
          })

          return
        }

        this.updateSelectedCompany({ ...company.data, plan })
      } catch (error) {
        catchError(error)
      }
    },

    async costCenterCounter (companyId) {
      try {
        const { data: res } = await this.$apiService.costCenter.get(
          { limit: 1 },
          { page: 1 },
          { company: companyId }
        )
        this.costCenterLength = res.length
      } catch (error) {
        catchError(error)
      }
    },

    async getDefaultCostCenter (companyId) {
      try {
        const { data: res } = await this.$apiService.costCenter.get(
          { limit: 5 },
          { page: 1 },
          { company: companyId },
          { search: 'padr' }
        )

        if (res && res.data && this.isValidArray(res.data)) {
          this.hasDefaultCostCenter = Boolean(res.data.find(item => item.name === 'Padrão'))
        }
      } catch (error) {
        catchError(error)
      }
    },

    async employeesCounter (companyId) {
      try {
        const { data: res } = await this.$apiService.employee.get(
          { limit: 1 },
          { page: 1 },
          { company: companyId }
        )
        this.employeesLength = res ? res.length : 0
      } catch (error) {
        catchError(error)
      }
    },

    async pendoInitialize (user, claims) {
      if (process.env.NODE_ENV !== 'production') return

      try {
        const companiesIds = user.business.isAdministrator ? 1 : user.business.companies
        const permission = claims.find(item => item === 'ROLE_CORPORATE_USER') ? 'CorporateUser' : user.type
        const ROLE_EMPLOYEE_USER = this.hasRole('profiles', 'ROLE_EMPLOYEE_USER')
        const ROLE_GET_COMPANY_FINANCIALCONFIG = this.hasRole('company', 'ROLE_GET_COMPANY_FINANCIALCONFIG')
        const webCall = this.hasRole('webCall', 'ROLE_POST_WEBCALL')

        let hasServiceBilling = null
        let isPending = null
        let useCreditCard = null
        let id = null
        let name = null
        let plan = null

        if (!ROLE_EMPLOYEE_USER && ROLE_GET_COMPANY_FINANCIALCONFIG) {
          const { data: res } = await this.$apiService.company.getCompaniesFinancialConfig(companiesIds)
          const companies = res.data.companies[0]

          hasServiceBilling = companies.paymentHasServiceBilling
          isPending = companies.paymentIsPending
          useCreditCard = companies.paymentUseCreditCard
          id = companies.companyId
          name = companies.companyName
          plan = companies.pod
        }

        pendoInitialize({
          user: {
            id: user.business.legacyUserId,
            permission,
            email: user.business.email,
            webCall
          },
          payment: {
            hasServiceBilling,
            isPending,
            useCreditCard
          },
          companyInfo: {
            id,
            name,
            plan
          }
        })
      } catch (error) {
        catchError(error)
      }
    },

    redirectToPrivateRoute () {
      try {
        const ROLE_POST_COSTCENTER = this.hasRole('costCenter', 'ROLE_POST_COSTCENTER')
        const ROLE_EMPLOYEE_WIZARD = this.hasRole('employee', 'ROLE_EMPLOYEE_WIZARD')
        const redirectOnAuth = localStorage.getItem(REDIRECT_PATH_ON_AUTH)

        if (ROLE_POST_COSTCENTER && this.globalCompany && this.costCenterLength === 0) {
          this.$router.push({ name: 'Welcome' }).catch(e => {})
        } else if (
          ROLE_EMPLOYEE_WIZARD &&
          this.hasDefaultCostCenter &&
          this.employeesLength <= 1 &&
          this.globalCompany.plan.toLowerCase() === 'smb' && !localStorage.getItem(SESSION_SKIP_WIZARD)
        ) {
          this.$router.push({ name: 'Wizard' }).catch(e => {})
        } else if (redirectOnAuth) {
          localStorage.removeItem(REDIRECT_PATH_ON_AUTH)
          this.$router.push(redirectOnAuth).catch(e => {})
        } else {
          this.$router.push({ name: 'Dashboard' }).catch(e => {})
        }
      } catch (error) {
        catchError(error)
      }
    },

    gaEventDispatch (label, obj) {
      this.$ga.event(label, 'submit', JSON.stringify(obj))
    },

    getAttemptsRemaining (str) {
      const matchUserBlocked = /usu(?:á|a)rio(?:s)?\s+bloqueado(?:s)?/i.test(str)
      const matchAttempt = /.*(\d+).*/.exec(str)

      if (matchUserBlocked) {
        this.$emit('setAttemptsRemaining', 0)
        return
      }

      if (this.isValidArray(matchAttempt) && matchAttempt.length > 1) {
        const attempt = Number(matchAttempt[1])
        this.$emit('setAttemptsRemaining', attempt)
        return
      }

      this.$emit('setAttemptsRemaining', '')
      this.showAlert({ type: 'error', message: str })
    },

    async getWalletFinancialConfig (company) {
      try {
        const { data: walletFinancialConfig } = await this.$apiService.company.getWalletFinancialConfig(company.id)
        const { financialAccountId = null, periodicityConfigType = null, paymentMethod = null } = walletFinancialConfig.content

        this.updateSelectedCompany({
          ...company,
          financialAccountId,
          periodicityConfigType,
          paymentMethod
        })
      } catch (error) {
        catchError(error)
      }
    }
  }
}
</script>
